import React, { useState, useEffect, useRef } from 'react';
import { Bell, Award, Check } from 'lucide-react';
import { Button } from './ui/button';
import api from '../api';

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchCount = async () => {
    try {
      const res = await api.get('/kudos/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch { /* silent */ }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get('/kudos/notifications');
      setNotifications(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const toggleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const markRead = async () => {
    try {
      await api.put('/kudos/notifications/mark-read');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const BADGE_COLORS = {
    teamwork: '#2563eb', innovation: '#7c3aed', leadership: '#b45309', helpfulness: '#dc2626',
    above_beyond: '#059669', problem_solver: '#0891b2', mentor: '#4f46e5', customer_champion: '#ca8a04',
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggleOpen}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell">
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse"
            data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-3 border-b bg-slate-50">
            <span className="text-sm font-semibold text-slate-700">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={markRead} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1" data-testid="mark-all-read-btn">
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-400">Loading...</div>
            ) : notifications.length > 0 ? (
              notifications.map(n => (
                <div key={n.id}
                  className={`p-3 border-b last:border-0 transition-colors ${!n.read ? 'bg-indigo-50/50' : 'hover:bg-slate-50'}`}
                  data-testid={`notification-${n.id}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${BADGE_COLORS[n.badge] || '#6366f1'}20` }}>
                      <Award className="h-4 w-4" style={{ color: BADGE_COLORS[n.badge] || '#6366f1' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2" />}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">No notifications yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
